/**
 * <dm-reviews> — images-first product reviews (sections/dm-pdp-reviews.liquid, Worker mode).
 * Design contract: Design - SOPs/dm-reviews.md.
 *
 * Talks only to the reviews Worker (settings.dm_reviews_endpoint), never to Trustoo; the
 * Trustoo token lives in the Worker. Three GET endpoints, all keyed by the Shopify product id:
 *   /api/reviews/overview  { rating|null, summary|null, photo_count }
 *   /api/reviews/photos    { total, page, per_page, photos: [{ url, thumb, review }] }
 *   /api/reviews           { total, page, per_page, reviews: [Review] }  (+ rating / with_photos)
 *
 * Rules this file keeps:
 *  - No API string is ever parsed as HTML. Text reaches the DOM through textContent, text
 *    nodes and attribute setters; there is no innerHTML / insertAdjacentHTML in this file.
 *    Image URLs must parse as http(s) or they are dropped.
 *  - The server-rendered content (metafield rating and up to three reviews) is the fallback.
 *    A failed, malformed or slow (> 6 s) answer leaves it in place and logs one console.warn.
 *  - Nothing is fetched until the section is within FETCH_MARGIN (2000px) of the viewport, so
 *    the PDP above the fold never waits on reviews, yet the answers usually land while the
 *    section is still off screen. The three requests then run in parallel.
 *  - The server's first paint already holds the loading layout (skeletons, a skeleton rating
 *    line, invisible [data-rv-pending] rows for the chips and the two "more" buttons), so
 *    neither the element upgrading nor data landing shifts the page. Every path below
 *    settles those placeholders: filled, or removed when there is nothing to put there.
 *  - The Worker can need less room than the loading layout reserved (3 photos where nine
 *    skeleton tiles stood, no summary, an empty list). Such a region only shrinks where the
 *    reader cannot see it move (#shrink): while it is on screen its old height is held until
 *    it has scrolled out of view.
 */

const TIMEOUT_MS = 6000;
// Start fetching this far ahead of the viewport (about 2.5 phone screens), so a slow Worker
// usually answers before the reader reaches the section.
const FETCH_MARGIN = '2000px 0px';
// Scroll anchoring keeps the view still when content above it shrinks (Chromium, Firefox).
const ANCHORING = Boolean(window.CSS?.supports?.('overflow-anchor', 'auto'));
// Anchoring is suppressed while an ancestor transitions (the section's 500ms scroll reveal):
// a region above the viewport is re-tried this often, this many times, before falling back.
const ANCHOR_RETRY_MS = 600;
const ANCHOR_RETRIES = 3;
const PHOTOS_PER_PAGE = 12;
const WIDE = '(min-width: 750px)';
// Reviews per page. Keep in sync with sections/dm-pdp-reviews.liquid: it renders up to 6
// skeleton cards and hides the 5th and 6th below 750px (the WIDE query).
const PER_PAGE_NARROW = 4;
const PER_PAGE_WIDE = 6;
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown']);

const lang = () => document.documentElement.lang || 'en';

let numberFormat;
const fmt = (n) => {
  numberFormat ||= new Intl.NumberFormat(lang());
  return numberFormat.format(n);
};

const plural = (n, one, many) => `${fmt(n)} ${n === 1 ? one : many}`;

const ratingText = (r) => (Number.isInteger(r) ? String(r) : r.toFixed(1));

/** A string from API data, trimmed and length-capped; anything else becomes ''. */
function str(value, max = 6000) {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' ? value.trim().slice(0, max) : '';
}

/** Only absolute http(s) URLs survive. */
function safeUrl(value) {
  const raw = str(value, 2048);
  if (!raw) return '';
  try {
    const url = new URL(raw, window.location.href);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
  } catch {
    return '';
  }
}

let dateFormat;
function formatDate(iso) {
  const raw = str(iso, 40);
  if (!raw) return '';
  // A date-time without a zone would parse as the viewer's local time and, formatted in UTC,
  // show the previous day east of UTC. Read it as UTC, like date-only strings.
  const zoneless = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(raw);
  const date = new Date(zoneless ? `${raw}Z` : raw);
  if (Number.isNaN(date.getTime())) return '';
  dateFormat ||= new Intl.DateTimeFormat(lang(), { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return dateFormat.format(date);
}

let regionNames;
function countryName(code) {
  if (!/^[A-Z]{2}$/.test(code)) return '';
  try {
    regionNames ||= new Intl.DisplayNames([lang()], { type: 'region' });
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
}

const altFor = (review) => (review?.author ? `Photo from ${review.author}’s review` : 'Photo from a customer review');

/** Normalise a contract Review. Unknown or unsafe fields fall back to empty values. */
function normalizeReview(raw, key) {
  if (!raw || typeof raw !== 'object') return null;
  const rating = Math.min(5, Math.max(0, Math.round((Number(raw.rating) || 0) * 10) / 10));
  const media = Array.isArray(raw.media)
    ? raw.media
        .map((m) => {
          if (!m || m.type !== 'image') return null;
          const url = safeUrl(m.url);
          return url ? { url, thumb: safeUrl(m.thumb) || url } : null;
        })
        .filter(Boolean)
    : [];
  const replyBody = raw.reply && typeof raw.reply === 'object' ? str(raw.reply.body) : '';
  const id = str(raw.id, 120);
  return {
    key: key || (id ? `id-${id}` : `anon-${Math.random().toString(36).slice(2)}`),
    rating,
    title: str(raw.title, 300),
    body: str(raw.body),
    author: str(raw.author, 80),
    country: str(raw.country, 2).toUpperCase(),
    verified: raw.verified === true,
    date: str(raw.date, 40),
    media,
    reply: replyBody ? { body: replyBody, date: str(raw.reply.date, 40) } : null,
  };
}

/**
 * The summary as nodes: plain text with each highlight wrapped in <strong>. Highlights are
 * matched verbatim (then case-insensitively), never overlapping; one that is not in the text
 * is skipped rather than guessed at.
 */
function summaryNodes(text, highlights) {
  const ranges = [];
  const lower = text.toLowerCase();
  for (const h of Array.isArray(highlights) ? highlights : []) {
    const phrase = str(h, 200);
    if (phrase.length < 2) continue;
    let at = text.indexOf(phrase);
    if (at < 0) at = lower.indexOf(phrase.toLowerCase());
    if (at < 0) continue;
    const end = at + phrase.length;
    if (ranges.some((r) => at < r.end && end > r.start)) continue;
    ranges.push({ start: at, end });
  }
  ranges.sort((a, b) => a.start - b.start);

  const frag = document.createDocumentFragment();
  let pos = 0;
  for (const r of ranges) {
    if (r.start > pos) frag.append(text.slice(pos, r.start));
    const strong = document.createElement('strong');
    strong.textContent = text.slice(r.start, r.end);
    frag.append(strong);
    pos = r.end;
  }
  if (pos < text.length) frag.append(text.slice(pos));
  return frag;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/** The first rendered element after `node` in document order: it moves with all content below `node`. */
function nextRendered(node) {
  for (let n = node; n && n !== document.body; n = n.parentElement) {
    for (let s = n.nextElementSibling; s; s = s.nextElementSibling) {
      if (s.getClientRects().length) return s;
    }
  }
  return null;
}

/** True while an ancestor of `node` runs a CSS transition or animation (e.g. the section's scroll reveal). */
function animating(node) {
  for (let n = node.parentElement; n; n = n.parentElement) {
    if (n.getAnimations?.().some((a) => a.playState === 'running')) return true;
  }
  return false;
}

/** Scroll by `dy` at once (the theme sets html { scroll-behavior: smooth }). */
function scrollInstant(dy) {
  const root = document.documentElement;
  const prev = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  window.scrollTo(window.scrollX, window.scrollY + dy);
  root.style.scrollBehavior = prev;
}

let uid = 0;
const nextId = (prefix) => `${prefix}-${++uid}`;

class DmReviews extends HTMLElement {
  #endpoint = '';
  #productId = '';
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {Map<string, ReturnType<typeof normalizeReview>>} */
  #byKey = new Map();
  #ssr = [];

  #liveRating = false;
  #summaryShown = false;
  /**
   * The Worker writes a product's summary on first request (and again when its 24 h cache
   * lapses); past its inline budget it answers `summary: null` and finishes in the
   * background. So a product with enough reviews asks again — twice, 4 s then 9 s later —
   * while the summary skeleton keeps its place, instead of dropping the box for that
   * visitor (owner 2026-10-07: leash page showed no summary).
   */
  #summaryRetries = 0;
  #summaryTimer = 0;
  /** @type {{ text: string, highlights: unknown } | null} */
  #summary = null;

  #photos = [];
  #photosTotal = 0;
  #photosPage = 0;
  #photosVisible = 0;
  #feature = false;
  /** Column count the grid was last rendered for (0 = still the skeleton). */
  #gridCols = 0;
  /** @type {Promise<boolean> | null} */
  #photosPending = null;

  #filter = 'all';
  /** The filter whose cards the list holds (#filter can run ahead while a request is out). */
  #shownFilter = 'all';
  #perPage = 0;
  #reviewsPage = 0;
  #reviewsLoaded = 0;
  #reviewsTotal = 0;
  #reviewsToken = 0;
  #reviewsLive = false;

  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  #lb = { items: [], index: 0, total: () => 0, more: null, opener: null, busy: false };
  #lockedScroll = false;
  #touchX = 0;
  #touchY = 0;
  #lastWidth = 0;
  #resizeRaf = 0;

  /** @type {Map<HTMLElement, { height: number, hide: boolean, tries: number, timer: number }>} regions holding their old height */
  #held = new Map();
  /** @type {IntersectionObserver | null} releases a held region once it has left the viewport */
  #heldIo = null;

  connectedCallback() {
    if (this.dataset.rvInit) return;
    this.dataset.rvInit = 'true';

    this.#readSsr();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#bindLightbox();
    this.#bindApp();
    this.#measureCards(this.#all('[data-rv-list] .dm-rv-card'));
    this.#lastWidth = window.innerWidth;
    window.addEventListener('resize', this.#onResize, { passive: true });

    this.#endpoint = (this.dataset.endpoint || '').trim().replace(/\/+$/, '');
    this.#productId = (this.dataset.productId || '').trim();
    let valid = /^\d+$/.test(this.#productId);
    try {
      valid = valid && /^https?:$/.test(new URL(this.#endpoint).protocol);
    } catch {
      valid = false;
    }
    if (!valid) {
      this.#fallbackAll(new Error('reviews endpoint or product id is missing or invalid'));
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.#start();
      return;
    }
    this.#io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#io?.disconnect();
        this.#io = null;
        this.#start();
      },
      { rootMargin: FETCH_MARGIN }
    );
    this.#io.observe(this);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#summaryTimer);
    this.#io?.disconnect();
    this.#io = null;
    this.#heldIo?.disconnect();
    this.#heldIo = null;
    for (const [region, hold] of this.#held) {
      window.clearTimeout(hold.timer);
      this.#unhold(region, hold);
    }
    this.#held.clear();
    window.removeEventListener('resize', this.#onResize);
    if (this.#dialog?.open) this.#dialog.close();
  }

  /* ------------------------------------------------------------------ plumbing */

  #q(selector) {
    return this.querySelector(selector);
  }

  #all(selector) {
    return Array.from(this.querySelectorAll(selector));
  }

  #warn(where, err) {
    console.warn(`[dm-reviews] ${where}: ${err?.message || err} — keeping the server-rendered reviews.`);
  }

  #announce(message) {
    const live = this.#q('[data-rv-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => {
      live.textContent = message;
    }, 60);
  }

  /* ------------------------------------------------------------------ no layout shift */

  /**
   * Run `change` (a Worker-driven update that may leave `region` smaller than the loading
   * layout reserved); with `hide`, then remove the region. Its space closes only where that
   * moves nothing the reader sees:
   *  - below the viewport: at once;
   *  - on screen: not yet. The old height is held (min-height; a removed region stays as an
   *    invisible box, [data-rv-parked]) until the region has scrolled out of view;
   *  - above the viewport: at once if scroll anchoring keeps the view still (measured, not
   *    assumed), else held and retried (see #release).
   * Only for the first load. Changes the viewer asks for (filters, "more" buttons) apply at once.
   */
  #shrink(region, change = null, hide = false) {
    if (!region) return;
    const held = this.#held.get(region);
    if (held) {
      // Already holding its first height: apply, and remember how it should end.
      change?.();
      if (hide) {
        held.hide = true;
        region.setAttribute('data-rv-parked', '');
      }
      return;
    }
    const height = region.hidden ? 0 : region.getBoundingClientRect().height;
    change?.();
    if (!height) {
      // Not rendered (hidden, or inside a hidden ancestor): nothing can move.
      if (hide) region.hidden = true;
      return;
    }
    if (hide) region.setAttribute('data-rv-parked', '');
    else if (region.getBoundingClientRect().height >= height - 0.5) return; // did not shrink
    region.style.minHeight = `${height}px`;
    const hold = { height, hide, tries: 0, timer: 0 };
    this.#held.set(region, hold);
    if (!this.#release(region)) {
      this.#heldIo ||= new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const target = entry.target;
          // A pending retry (see #release) re-checks on its own timer.
          if (!entry.isIntersecting && target instanceof HTMLElement && !this.#held.get(target)?.timer) this.#release(target);
        }
      });
      this.#heldIo.observe(region);
    }
  }

  /** Close a held region's space if the reader cannot see anything move. Returns true when closed. */
  #release(region) {
    const hold = this.#held.get(region);
    if (!hold) return true;
    window.clearTimeout(hold.timer);
    hold.timer = 0;
    // Inside a section removed meanwhile, or below the viewport: nothing the reader sees moves.
    const box = region.getBoundingClientRect();
    if (!region.getClientRects().length || box.top >= window.innerHeight) return this.#letGo(region);
    if (box.bottom > 0) return false; // on screen: keep holding

    // Above the viewport: everything the reader sees sits below the region, and scroll
    // anchoring keeps it still as the space closes. Not while an ancestor animates (the
    // section's 500ms scroll reveal): anchoring may not apply, and Chromium counts the shift
    // even when it does. Holding a little longer is invisible up there, so wait that out.
    const retry = () => {
      hold.tries += 1;
      hold.timer = window.setTimeout(() => this.#release(region), ANCHOR_RETRY_MS);
      return false;
    };
    const canRetry = ANCHORING && hold.tries < ANCHOR_RETRIES;
    if (canRetry && animating(region)) return retry();
    // Measure whether the view stayed put (anchoring applies during the layout this read forces).
    const ref = nextRendered(region);
    const before = ref ? ref.getBoundingClientRect().top : 0;
    this.#unhold(region, hold);
    const moved = ref ? ref.getBoundingClientRect().top - before : 0;
    if (Math.abs(moved) >= 1 && canRetry) {
      // Anchoring was suppressed: put the hold back in this same task (no frame shows the move).
      this.#rehold(region, hold);
      return retry();
    }
    // No scroll anchoring (Safari): scroll by what the content moved, so the view stays still.
    if (Math.abs(moved) >= 1) scrollInstant(moved);
    return this.#letGo(region);
  }

  /** Stop holding `region` (closing its space now). Also used when the viewer changes it. */
  #letGo(region) {
    const hold = region && this.#held.get(region);
    if (!hold) return true;
    window.clearTimeout(hold.timer);
    this.#unhold(region, hold);
    this.#held.delete(region);
    this.#heldIo?.unobserve(region);
    return true;
  }

  #unhold(region, hold) {
    region.style.minHeight = '';
    region.removeAttribute('data-rv-parked');
    if (hold.hide) region.hidden = true;
  }

  #rehold(region, hold) {
    if (hold.hide) {
      region.hidden = false;
      region.setAttribute('data-rv-parked', '');
    }
    region.style.minHeight = `${hold.height}px`;
  }

  /** Visible and staying: neither hidden nor parked (removed, waiting to scroll out of view). */
  #isShown(node) {
    return Boolean(node) && !node.hidden && !node.hasAttribute('data-rv-parked');
  }

  async #get(path, params = {}) {
    const url = new URL(this.#endpoint + path);
    url.searchParams.set('product_id', this.#productId);
    for (const [k, v] of Object.entries(params)) url.searchParams.set(k, String(v));

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const res = await fetch(url.href, {
        signal: controller.signal,
        mode: 'cors',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`${path} answered HTTP ${res.status}`);
      const data = await res.json();
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error(`${path} returned an unexpected body`);
      return data;
    } catch (err) {
      if (err?.name === 'AbortError') throw new Error(`${path} timed out after ${TIMEOUT_MS / 1000}s`);
      throw err;
    } finally {
      window.clearTimeout(timer);
    }
  }

  #readSsr() {
    const script = this.#q('script[data-rv-ssr]');
    if (!script) return;
    try {
      const list = JSON.parse(script.textContent || '[]');
      if (!Array.isArray(list)) return;
      list.forEach((raw, i) => {
        const review = normalizeReview(raw, `ssr-${i}`);
        if (!review) return;
        this.#ssr.push(review);
        this.#byKey.set(review.key, review);
      });
    } catch (err) {
      this.#warn('server review data', err);
    }
  }

  /* ------------------------------------------------------------------ loading */

  async #start() {
    this.dataset.rvState = 'loading';
    await Promise.allSettled([
      this.#get('/api/reviews/overview')
        .then((data) => this.#applyOverview(data))
        .catch((err) => this.#failOverview(err)),
      this.#get('/api/reviews/photos', { page: 1, per_page: PHOTOS_PER_PAGE })
        .then((data) => this.#applyPhotos(data, true))
        .catch((err) => this.#failPhotos(err)),
      this.#loadReviews(true, true),
    ]);
    this.dataset.rvState = 'done';
    this.#collapseIfEmpty();
  }

  /** No endpoint (or an invalid one): settle every region on the server data. */
  #fallbackAll(err) {
    this.#warn('setup', err);
    this.#failOverview(null);
    this.#failPhotos(null);
    this.#failReviews(null, true, true);
    this.dataset.rvState = 'fallback';
    this.#collapseIfEmpty();
  }

  /** True only after the Worker answered with zero reviews for this product. */
  #confirmedEmpty = false;

  /**
   * "No reviews yet — be the first" (section setting show_empty_state). Shown only when the
   * Worker CONFIRMED zero reviews and nothing was server-rendered, so an outage never claims a
   * product has no reviews. Returns true when the card is showing.
   */
  #showEmpty() {
    const box = this.#q('[data-rv-empty]');
    if (!(box instanceof HTMLElement) || !this.#confirmedEmpty) return false;
    if (this.#ssr.length || this.#photosTotal > 0) return false;
    // The photo grid and review list were reserved at skeleton height for the loading paint.
    // #shrink only closes a region while it is off screen (no shift under the reader), so a
    // confirmed-empty product kept ~2,000px of invisible space at 375. The card replaces them
    // at once: a compact card is a better shift than a screen of blank space.
    for (const sel of ['[data-rv-photos]', '[data-rv-reviews]', '[data-rv-summary]']) {
      const region = this.#q(sel);
      if (region instanceof HTMLElement) region.hidden = true;
    }
    box.hidden = false;
    return true;
  }

  /** A section with nothing server-rendered and nothing from the Worker hides itself. */
  #collapseIfEmpty() {
    if (this.#showEmpty()) return;
    if (!this.hasAttribute('data-rv-ssr-empty')) return;
    const regions = ['[data-rv-rating]', '[data-rv-summary]', '[data-rv-photos]', '[data-rv-reviews]'];
    if (regions.some((sel) => this.#isShown(this.#q(sel)))) return;
    const section = this.closest('.dm-reviews');
    if (section instanceof HTMLElement) this.#shrink(section, null, true);
  }

  /* ------------------------------------------------------------------ 1 · rating + 2 · summary */

  #applyOverview(data) {
    const rating = data.rating && typeof data.rating === 'object' ? data.rating : null;
    let rated = false;
    if (rating) {
      const count = Math.max(0, Math.floor(Number(rating.count) || 0));
      const average = Number(rating.average);
      rated = count > 0 && average > 0 && average <= 5;
      if (rated) this.#setRating(average, count);
    }
    if (!rated) this.#settleRating();

    const summary = data.summary && typeof data.summary === 'object' ? data.summary : null;
    const text = summary ? str(summary.text, 600) : '';
    if (!text) {
      // Three reviews is the Worker's minimum (MIN_QUALIFYING): below it no summary is coming.
      const count = rating ? Math.floor(Number(rating.count) || 0) : 0;
      if (count >= 3 && this.#summaryRetries < 2 && !this.#summaryShown) {
        const delay = this.#summaryRetries === 0 ? 4000 : 9000;
        this.#summaryRetries += 1;
        window.clearTimeout(this.#summaryTimer);
        this.#summaryTimer = window.setTimeout(() => {
          if (!this.isConnected) return;
          this.#get('/api/reviews/overview')
            .then((next) => this.#applyOverview(next))
            .catch(() => this.#hideSummary());
        }, delay);
        return;
      }
      this.#hideSummary();
      return;
    }

    const box = this.#q('[data-rv-summary]');
    const note = this.#q('[data-rv-summary-note]');
    if (!box || !note) return;
    const basedOn = Math.max(0, Math.floor(Number(summary.based_on) || 0));
    note.textContent = basedOn > 0 ? `AI summary of ${plural(basedOn, 'review', 'reviews')}` : 'AI summary of customer reviews';
    box.classList.remove('is-loading');
    box.removeAttribute('aria-busy');
    box.hidden = false;
    this.#summary = { text, highlights: summary.highlights };
    this.#summaryShown = true;
    this.#fitSummary();
    document.fonts?.ready.then(() => this.#fitSummary()).catch(() => {});
  }

  /**
   * Two lines, whole words. The CSS line clamp alone would cut mid-word ("bubblegu…"), so when
   * the text is taller than two lines the visible copy is shortened to the most whole words that
   * fit plus an ellipsis, and screen readers get the full sentence from a hidden twin instead.
   * Re-run whenever the width changes.
   */
  #fitSummary() {
    const body = this.#q('[data-rv-summary-text]');
    const twin = this.#q('[data-rv-summary-full]');
    if (!body || !this.#summary) return;
    const { text, highlights } = this.#summary;
    const fits = () => body.scrollHeight <= body.clientHeight + 1;

    body.replaceChildren(summaryNodes(text, highlights));
    body.removeAttribute('aria-hidden');
    if (twin) twin.textContent = '';
    if (fits()) return;

    const words = text.split(/\s+/);
    const shorten = (n) => `${words.slice(0, n).join(' ').replace(/[\s,;:.!?\u2013\u2014-]+$/, '')}\u2026`;
    let lo = 1;
    let hi = words.length - 1;
    let best = 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      body.replaceChildren(summaryNodes(shorten(mid), highlights));
      if (fits()) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    // "…in the hand. The…" reads as a glitch: when only a few words of the next sentence would
    // show, end on the full stop instead (no ellipsis).
    let visible = shorten(best);
    const kept = words.slice(0, best).join(' ');
    const stop = Math.max(kept.lastIndexOf('. '), kept.lastIndexOf('! '), kept.lastIndexOf('? '));
    if (stop > 0 && kept.slice(stop + 2).split(/\s+/).filter(Boolean).length <= 3) visible = kept.slice(0, stop + 1);
    body.replaceChildren(summaryNodes(visible, highlights));
    if (twin) {
      body.setAttribute('aria-hidden', 'true');
      twin.textContent = text;
    }
  }

  #setRating(average, count) {
    const row = this.#q('[data-rv-rating]');
    if (!row) return;
    const avg = average.toFixed(1);
    const stars = this.#q('[data-rv-stars]');
    stars?.style.setProperty('--dm-rv-fill', `${Math.round((average / 5) * 1000) / 10}%`);
    const avgNode = this.#q('[data-rv-avg]');
    if (avgNode) avgNode.textContent = avg;
    const countNode = this.#q('[data-rv-count]');
    if (countNode) countNode.textContent = plural(count, 'review', 'reviews');
    const label = this.#q('[data-rv-rating-label]');
    if (label) label.textContent = `Rated ${avg} out of 5 from ${plural(count, 'review', 'reviews')}`;
    this.#q('[data-rv-rating-skel]')?.remove();
    row.classList.remove('is-loading');
    row.hidden = false;
    this.#liveRating = true;
  }

  /**
   * No rating from the Worker. A metafield rating stays as painted; the skeleton row (no
   * metafield rating) is removed — the only case where the header changes height.
   */
  #settleRating() {
    const row = this.#q('[data-rv-rating]');
    if (row?.classList.contains('is-loading')) this.#shrink(row, null, true);
  }

  #hideSummary() {
    const box = this.#q('[data-rv-summary]');
    if (box && !this.#summaryShown) this.#shrink(box, null, true);
  }

  #failOverview(err) {
    if (err) this.#warn('overview', err);
    this.#settleRating();
    this.#hideSummary();
  }

  /* ------------------------------------------------------------------ 3 · photos */

  #cssInt(node, prop, fallback) {
    const value = parseInt(getComputedStyle(node).getPropertyValue(prop), 10);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  #firstViewCount(grid) {
    const cols = this.#cssInt(grid, '--dm-rv-cols', 2);
    const rows = this.#cssInt(grid, '--dm-rv-rows', 4);
    return cols * rows - (this.#feature ? 3 : 0);
  }

  /** The smallest tile count >= n that ends on a full row (the 2x2 feature fills 4 cells). */
  #fullRows(n, cols) {
    if (n <= 0) return 0;
    const extra = this.#feature ? 3 : 0;
    return Math.ceil((n + extra) / cols) * cols - extra;
  }

  #stepCount(grid) {
    return this.#cssInt(grid, '--dm-rv-cols', 2) * this.#cssInt(grid, '--dm-rv-step-rows', 3);
  }

  #normalizePhoto(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const url = safeUrl(raw.url);
    if (!url) return null;
    let review = normalizeReview(raw.review);
    if (review) {
      // One review can own several photos: keep a single object per review key.
      review = this.#byKey.get(review.key) || review;
      this.#byKey.set(review.key, review);
    } else {
      review = normalizeReview({ rating: 0 });
    }
    return { url, thumb: safeUrl(raw.thumb) || url, review };
  }

  /** Appends one page; returns how many usable photos it added. */
  #addPhotos(data) {
    const list = Array.isArray(data.photos) ? data.photos : [];
    let added = 0;
    for (const raw of list) {
      const photo = this.#normalizePhoto(raw);
      if (!photo) continue;
      this.#photos.push(photo);
      added += 1;
    }
    const total = Math.max(0, Math.floor(Number(data.total) || 0));
    this.#photosTotal = Math.max(total, this.#photos.length);
    // A page that adds nothing ends paging, whatever `total` claims.
    if (!added) this.#photosTotal = this.#photos.length;
    this.#photosPage = Math.max(this.#photosPage + 1, Math.floor(Number(data.page) || 0));
    return added;
  }

  /** First page of photos. Fewer than the skeleton reserved (or none) shrink via #shrink. */
  #applyPhotos(data) {
    this.#addPhotos(data);
    if (!this.#photos.length) {
      this.#hidePhotos();
      return;
    }
    this.#shrink(this.#q('[data-rv-photos]'), () => this.#renderGrid());
  }

  /**
   * (Re)draw the grid for the current column count. The 2x2 feature tile spans two rows, so it
   * is used only when there are enough photos to fill both: 2 x cols - 3 tiles beside it, and
   * never fewer than 5 photos in all (5 at 2 or 4 columns, 9 at 6). Otherwise plain tiles.
   * A redraw after a breakpoint change keeps what the viewer had revealed, on full rows.
   */
  #renderGrid() {
    const grid = this.#q('[data-rv-grid]');
    if (!grid) return;
    const cols = this.#cssInt(grid, '--dm-rv-cols', 2);
    const keep = this.#photosVisible;
    const active = document.activeElement;
    const focusIndex = active instanceof HTMLElement && grid.contains(active) ? active.dataset.rvIndex : undefined;

    this.#gridCols = cols;
    this.#feature = this.#photosTotal >= Math.max(5, 2 * cols - 3);
    grid.classList.toggle('dm-rv-grid--feature', this.#feature);
    grid.replaceChildren();
    this.#photosVisible = 0;
    this.#revealPhotos(Math.max(this.#firstViewCount(grid), this.#fullRows(keep, cols)));
    grid.removeAttribute('aria-busy');
    if (focusIndex !== undefined) grid.querySelector(`[data-rv-index="${focusIndex}"]`)?.focus();
  }

  /** Rotation / resize across a grid breakpoint: the feature rule and full rows depend on columns. */
  #syncGridColumns() {
    const grid = this.#q('[data-rv-grid]');
    if (!grid || !this.#gridCols) return;
    if (this.#cssInt(grid, '--dm-rv-cols', 2) !== this.#gridCols) this.#renderGrid();
  }

  #revealPhotos(target) {
    const grid = this.#q('[data-rv-grid]');
    if (!grid) return [];
    const end = Math.min(target, this.#photos.length);
    const frag = document.createDocumentFragment();
    const added = [];
    for (let i = this.#photosVisible; i < end; i++) {
      const tile = this.#tile(this.#photos[i], i);
      added.push(tile);
      frag.append(tile);
    }
    grid.append(frag);
    this.#photosVisible = Math.max(this.#photosVisible, end);
    this.#syncPhotoControls();
    return added;
  }

  #tile(photo, index) {
    const feature = this.#feature && index === 0;
    const li = el('li', `dm-rv-tile${feature ? ' dm-rv-tile--feature' : ''}`);
    const button = el('button', 'dm-rv-tile__btn');
    button.type = 'button';
    button.dataset.rvOpen = 'grid';
    button.dataset.rvIndex = String(index);
    button.setAttribute('aria-haspopup', 'dialog');

    const img = el('img', 'dm-rv-tile__img');
    img.src = feature ? photo.url : photo.thumb;
    img.alt = altFor(photo.review);
    img.width = feature ? 800 : 400;
    img.height = img.width;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.addEventListener('error', () => li.classList.add('is-broken'), { once: true });
    button.append(img);

    const rating = photo.review.rating;
    if (rating > 0) {
      button.append(el('span', 'dm-visually-hidden', `, rated ${ratingText(rating)} out of 5`));
      const badge = el('span', 'dm-rv-tile__rating', `★ ${ratingText(rating)}`);
      badge.setAttribute('aria-hidden', 'true');
      button.append(badge);
    }
    li.append(button);
    return li;
  }

  #syncPhotoControls() {
    const count = this.#q('[data-rv-photos-count]');
    if (count) count.textContent = plural(this.#photosTotal, 'photo', 'photos');
    const more = this.#q('[data-rv-photos-more]');
    if (!more) return;
    const remaining = Math.max(0, this.#photosTotal - this.#photosVisible);
    more.hidden = remaining === 0;
    more.removeAttribute('data-rv-pending');
    const rest = this.#q('[data-rv-photos-remaining]');
    if (rest) rest.textContent = remaining ? `(${fmt(remaining)})` : '';
  }

  /** Make sure at least `n` photos are loaded (or every photo there is). One request at a time. */
  #ensurePhotos(n) {
    if (this.#photos.length >= n || this.#photos.length >= this.#photosTotal) return Promise.resolve(true);
    if (this.#photosPending) return this.#photosPending;
    this.#photosPending = (async () => {
      try {
        while (this.#photos.length < n && this.#photos.length < this.#photosTotal) {
          const data = await this.#get('/api/reviews/photos', { page: this.#photosPage + 1, per_page: PHOTOS_PER_PAGE });
          if (!this.#addPhotos(data)) break;
        }
        return true;
      } catch (err) {
        this.#warn('more photos', err);
        return false;
      } finally {
        this.#photosPending = null;
        this.#syncPhotoControls();
      }
    })();
    return this.#photosPending;
  }

  async #morePhotos(button) {
    const grid = this.#q('[data-rv-grid]');
    if (!grid || button.getAttribute('aria-busy') === 'true') return;
    const target = this.#photosVisible + this.#stepCount(grid);
    button.setAttribute('aria-busy', 'true');
    const ok = await this.#ensurePhotos(target);
    button.removeAttribute('aria-busy');
    const added = this.#revealPhotos(target);
    if (!ok && !added.length) {
      this.#announce('More photos could not load. Please try again.');
      return;
    }
    this.#announce(`Showing ${fmt(this.#photosVisible)} of ${plural(this.#photosTotal, 'photo', 'photos')}`);
    if (button.hidden && added.length) added[0].querySelector('button')?.focus();
  }

  #hidePhotos() {
    // Settle the reserved "Show more photos" row too: the block may stay parked for a while.
    this.#shrink(this.#q('[data-rv-photos]'), () => this.#settle(this.#q('[data-rv-photos-more]'), false), true);
  }

  /** Worker photos unavailable: show the server-rendered reviews' photos, or nothing. */
  #failPhotos(err) {
    if (err) this.#warn('photos', err);
    if (this.#photos.length) return;
    for (const review of this.#ssr) {
      for (const m of review.media) this.#photos.push({ url: m.url, thumb: m.thumb, review });
    }
    this.#photosTotal = this.#photos.length;
    if (!this.#photos.length) {
      this.#hidePhotos();
      return;
    }
    this.#shrink(this.#q('[data-rv-photos]'), () => this.#renderGrid());
  }

  /* ------------------------------------------------------------------ 4 · review list */

  /** Fixed at first use, so paging never skips. Matches the skeleton count (see PER_PAGE_*). */
  #reviewsPerPage() {
    this.#perPage ||= window.matchMedia(WIDE).matches ? PER_PAGE_WIDE : PER_PAGE_NARROW;
    return this.#perPage;
  }

  /** Resolves true (applied), false (failed) or null (superseded by a newer list request). */
  async #loadReviews(reset, initial = false) {
    const token = ++this.#reviewsToken;
    const list = this.#q('[data-rv-list]');
    const filter = this.#filter;
    const page = reset ? 1 : this.#reviewsPage + 1;
    const params = { page, per_page: this.#reviewsPerPage() };
    if (filter === '5') params.rating = 5;
    if (filter === 'photos') params.with_photos = 1;
    if (!initial) list?.setAttribute('aria-busy', 'true');
    try {
      const data = await this.#get('/api/reviews', params);
      if (token !== this.#reviewsToken) return null;
      this.#applyReviews(data, reset, initial);
      if (reset) this.#shownFilter = filter;
      return true;
    } catch (err) {
      if (token !== this.#reviewsToken) return null;
      this.#failReviews(err, reset, initial);
      return false;
    } finally {
      if (token === this.#reviewsToken) list?.removeAttribute('aria-busy');
    }
  }

  /** Settle a reserved [data-rv-pending] row: shown, or removed. */
  /** Settle a reserved [data-rv-pending] row: shown, or removed (on first load via #shrink). */
  #settle(node, show, initial = false) {
    if (!node) return;
    node.removeAttribute('data-rv-pending');
    if (show) node.hidden = false;
    else if (initial) this.#shrink(node, null, true);
    else node.hidden = true;
  }

  #applyReviews(data, reset, initial) {
    const items = (Array.isArray(data.reviews) ? data.reviews : []).map((raw) => normalizeReview(raw)).filter(Boolean);
    const total = Math.max(0, Math.floor(Number(data.total) || 0));

    // The Worker has nothing for the unfiltered list: keep whatever the server rendered.
    if (initial && (!total || !items.length)) {
      // A successful answer with total 0 is a CONFIRMED empty product (unlike a failed request,
      // which proves nothing), so the "No reviews yet" card may show — see #showEmpty.
      if (!total) this.#confirmedEmpty = true;
      this.#failReviews(null, true, true);
      return;
    }

    const list = this.#q('[data-rv-list]');
    if (!list) return;
    const cards = [];
    const fill = () => {
      if (reset) {
        list.replaceChildren();
        list.removeAttribute('data-rv-loading');
        this.#reviewsLoaded = 0;
      }
      const frag = document.createDocumentFragment();
      for (const raw of items) {
        const review = this.#byKey.get(raw.key) || raw;
        this.#byKey.set(review.key, review);
        const li = el('li', 'dm-rv-list__item');
        const card = this.#card(review);
        li.append(card);
        cards.push(card);
        frag.append(li);
      }
      list.append(frag);
    };
    // First page: shorter cards than the skeletons must not pull the page up under the reader.
    if (initial) this.#shrink(list, fill);
    else fill();

    this.#reviewsLive = true;
    this.#reviewsPage = reset ? 1 : this.#reviewsPage + 1;
    this.#reviewsLoaded += items.length;
    this.#reviewsTotal = items.length ? Math.max(total, this.#reviewsLoaded) : this.#reviewsLoaded;
    this.#measureCards(cards);

    if (initial) this.#settle(this.#q('[data-rv-filters]'), total > 1, true);
    this.#settle(this.#q('[data-rv-list-more]'), this.#reviewsLoaded < this.#reviewsTotal, initial);

    const status = this.#q('[data-rv-list-status]');
    if (status) {
      status.hidden = this.#reviewsLoaded > 0;
      status.textContent = this.#reviewsLoaded > 0 ? '' : 'No reviews match this filter yet.';
    }
  }

  #failReviews(err, reset, initial) {
    if (err) this.#warn('reviews', err);
    if (initial) {
      // Keep the server-rendered cards; drop skeletons and the reserved chips / "Load more"
      // rows (both need the Worker); remove the block if nothing is left. All via #shrink.
      const list = this.#q('[data-rv-list]');
      const block = this.#q('[data-rv-reviews]');
      const trim = () => {
        for (const skel of this.#all('[data-rv-list] .dm-rv-list__item--skel')) skel.remove();
        list?.removeAttribute('data-rv-loading'); // server cards back to their own height
      };
      if (block && list && !list.querySelector(':scope > :not(.dm-rv-list__item--skel)')) {
        this.#shrink(block, () => {
          trim();
          this.#settle(this.#q('[data-rv-filters]'), false);
          this.#settle(this.#q('[data-rv-list-more]'), false);
        }, true);
        return;
      }
      this.#shrink(list, trim);
      this.#settle(this.#q('[data-rv-filters]'), false, true);
      this.#settle(this.#q('[data-rv-list-more]'), false, true);
      return;
    }
    const status = this.#q('[data-rv-list-status]');
    if (status) {
      status.textContent = 'Reviews could not load right now. Please try again.';
      status.hidden = false;
    }
  }

  async #moreReviews(button) {
    // While a filter change is out, the next page would belong to a list not shown yet.
    if (button.getAttribute('aria-busy') === 'true' || this.#filter !== this.#shownFilter) return;
    button.setAttribute('aria-busy', 'true');
    this.#letGo(this.#q('[data-rv-list]')); // the viewer is changing the list: no blank hold under it
    const before = this.#reviewsLoaded;
    const ok = await this.#loadReviews(false);
    button.removeAttribute('aria-busy');
    if (!ok) return;
    this.#announce(`Showing ${fmt(this.#reviewsLoaded)} of ${plural(this.#reviewsTotal, 'review', 'reviews')}`);
    if (button.hidden) {
      const firstNew = this.#all('[data-rv-list] > .dm-rv-list__item')[before];
      firstNew?.querySelector('.dm-rv-card')?.setAttribute('tabindex', '-1');
      firstNew?.querySelector('.dm-rv-card')?.focus();
    }
  }

  async #applyFilter(value) {
    if (!['all', 'photos', '5'].includes(value) || value === this.#filter) return;
    this.#filter = value;
    this.#letGo(this.#q('[data-rv-list]')); // the viewer is changing the list: no blank hold under it
    const ok = await this.#loadReviews(true);
    if (ok === null) return; // a newer filter owns the list now
    if (!ok) {
      // The list still holds the cards of the filter it showed before: put that chip back so
      // chip and list agree (#failReviews has already shown the error under the list).
      this.#filter = this.#shownFilter;
      const radio = this.#all('input[data-rv-filter]').find((input) => input.value === this.#shownFilter);
      if (radio instanceof HTMLInputElement) radio.checked = true;
      this.#announce('Reviews could not load right now. Please try again.');
      return;
    }
    this.#announce(
      this.#reviewsLoaded
        ? `Showing ${fmt(this.#reviewsLoaded)} of ${plural(this.#reviewsTotal, 'review', 'reviews')}`
        : 'No reviews match this filter yet.'
    );
  }

  /** Clone the server's card template and fill its slots with text. */
  #card(review, { full = false } = {}) {
    const template = this.#q('template[data-rv-tpl="card"]');
    const card = template?.content.firstElementChild?.cloneNode(true);
    if (!(card instanceof HTMLElement)) return el('article', 'dm-rv-card', review.body);
    const slot = (name) => card.querySelector(`[data-rv-slot="${name}"]`);
    card.dataset.rvKey = review.key;

    slot('stars')?.style.setProperty('--dm-rv-fill', `${review.rating * 20}%`);
    const ratingLabel = slot('rating');
    if (ratingLabel) ratingLabel.textContent = `${ratingText(review.rating)} out of 5 stars`;

    const title = slot('title');
    if (title) {
      title.textContent = review.title;
      title.hidden = !review.title;
    }

    const body = slot('body');
    const more = card.querySelector('[data-rv-more]');
    if (body) {
      body.textContent = review.body;
      body.id = nextId('dm-rv-body');
      if (full) body.classList.add('is-expanded');
    }
    if (more) {
      if (full || !body) more.remove();
      else more.setAttribute('aria-controls', body.id);
    }

    const media = slot('media');
    if (media) {
      media.replaceChildren();
      if (!full) {
        review.media.forEach((m, i) => {
          const li = el('li');
          const button = el('button', 'dm-rv-card__thumb');
          button.type = 'button';
          button.dataset.rvOpen = 'card';
          button.dataset.rvMedia = String(i);
          button.setAttribute('aria-haspopup', 'dialog');
          const img = el('img');
          img.src = m.thumb;
          img.alt = altFor(review);
          img.width = 112;
          img.height = 112;
          img.loading = 'lazy';
          img.decoding = 'async';
          button.append(img);
          li.append(button);
          media.append(li);
        });
      }
      media.hidden = !media.children.length;
    }

    const author = slot('author');
    if (author) {
      author.textContent = review.author;
      author.hidden = !review.author;
    }
    const country = slot('country');
    const place = countryName(review.country);
    if (country) {
      country.textContent = place;
      country.hidden = !place;
    }
    const byline = slot('byline');
    if (byline) byline.hidden = !review.author && !place;

    const verified = slot('verified');
    if (verified) verified.hidden = !review.verified;

    const date = slot('date');
    const when = formatDate(review.date);
    if (date) {
      date.textContent = when;
      date.hidden = !when;
      if (when) date.setAttribute('datetime', review.date);
      else date.removeAttribute('datetime');
    }

    const reply = slot('reply');
    if (reply) {
      const replyBody = slot('reply-body');
      if (replyBody) replyBody.textContent = review.reply ? review.reply.body : '';
      reply.hidden = !review.reply;
    }
    return card;
  }

  /** Show "Read more" only on cards whose text is actually clamped. */
  #measureCards(cards) {
    if (!cards.length) return;
    const run = () => {
      for (const card of cards) {
        const body = card.querySelector('[data-rv-slot="body"]');
        const more = card.querySelector('[data-rv-more]');
        if (!body || !more || body.classList.contains('is-expanded')) continue;
        more.hidden = !(body.scrollHeight > body.clientHeight + 1);
      }
    };
    window.requestAnimationFrame(run);
    document.fonts?.ready.then(run).catch(() => {});
  }

  #onResize = () => {
    if (window.innerWidth === this.#lastWidth) return;
    this.#lastWidth = window.innerWidth;
    window.cancelAnimationFrame(this.#resizeRaf);
    this.#resizeRaf = window.requestAnimationFrame(() => {
      this.#syncGridColumns();
      this.#fitSummary();
      this.#measureCards(this.#all('[data-rv-list] .dm-rv-card'));
    });
  };

  #toggleMore(button) {
    const body = document.getElementById(button.getAttribute('aria-controls') || '');
    if (!body) return;
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    body.classList.toggle('is-expanded', open);
    button.textContent = open ? 'Show less' : 'Read more';
  }

  /* ------------------------------------------------------------------ events */

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('[data-rv-lb]')) return;

    const opener = target.closest('[data-rv-open]');
    if (opener) {
      event.preventDefault();
      this.#openFrom(opener);
      return;
    }
    const more = target.closest('[data-rv-more]');
    if (more) {
      this.#toggleMore(more);
      return;
    }
    const photosMore = target.closest('[data-rv-photos-more]');
    if (photosMore) {
      this.#morePhotos(photosMore);
      return;
    }
    const listMore = target.closest('[data-rv-list-more]');
    if (listMore) this.#moreReviews(listMore);
  };

  #onChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('[data-rv-filter]') && input.checked) {
      this.#applyFilter(input.value);
    }
  };

  #bindApp() {
    const details = this.#q('[data-rv-app]');
    // Trustoo lays itself out on load; inside a closed <details> it measured zero width.
    details?.addEventListener('toggle', () => {
      if (details.open) window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    });
  }

  /* ------------------------------------------------------------------ lightbox */

  #openFrom(button) {
    if (button.dataset.rvOpen === 'grid') {
      const index = Number(button.dataset.rvIndex) || 0;
      this.#openLightbox(
        {
          items: this.#photos,
          total: () => Math.max(this.#photosTotal, this.#photos.length),
          more: () => this.#ensurePhotos(this.#photos.length + 1),
        },
        index,
        button
      );
      return;
    }
    const card = button.closest('[data-rv-key]');
    const review = card ? this.#byKey.get(card.getAttribute('data-rv-key')) : null;
    if (!review || !review.media.length) return;
    const items = review.media.map((m) => ({ url: m.url, thumb: m.thumb, review }));
    this.#openLightbox({ items, total: () => items.length, more: null }, Number(button.dataset.rvMedia) || 0, button);
  }

  #bindLightbox() {
    const dialog = this.#q('[data-rv-lb]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    this.#dialog = dialog;

    dialog.addEventListener('close', () => this.#onLightboxClose());
    dialog.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target === dialog) {
        dialog.close(); // backdrop / margin around the panel
      } else if (target.closest('[data-rv-lb-close]')) {
        dialog.close();
      } else if (target.closest('[data-rv-lb-prev]')) {
        this.#step(-1);
      } else if (target.closest('[data-rv-lb-next]')) {
        this.#step(1);
      }
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#step(1);
      } else if (event.key === 'Tab') {
        this.#trapFocus(event);
      } else if (SCROLL_KEYS.has(event.key)) {
        this.#scrollReview(event);
      }
    });

    const media = dialog.querySelector('[data-rv-lb-media]');
    media?.addEventListener(
      'touchstart',
      (event) => {
        const t = event.changedTouches[0];
        this.#touchX = t.clientX;
        this.#touchY = t.clientY;
      },
      { passive: true }
    );
    media?.addEventListener(
      'touchend',
      (event) => {
        const t = event.changedTouches[0];
        const dx = t.clientX - this.#touchX;
        const dy = t.clientY - this.#touchY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) this.#step(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  #openLightbox(set, index, opener) {
    const dialog = this.#dialog;
    if (!dialog || !set.items.length) return;
    this.#lb = { ...set, index: Math.min(Math.max(0, index), set.items.length - 1), opener, busy: false };
    this.#renderLightbox();
    if (!dialog.open) {
      dialog.showModal();
      const html = document.documentElement;
      this.#lockedScroll = !html.hasAttribute('scroll-lock');
      if (this.#lockedScroll) html.setAttribute('scroll-lock', '');
    }
    dialog.querySelector('[data-rv-lb-close]')?.focus();
  }

  #onLightboxClose() {
    if (this.#lockedScroll) document.documentElement.removeAttribute('scroll-lock');
    this.#lockedScroll = false;
    const opener = this.#lb.opener;
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    const img = this.#dialog?.querySelector('[data-rv-lb-img]');
    img?.removeAttribute('src');
  }

  #canNext() {
    const { items, index, total, more } = this.#lb;
    if (index < items.length - 1) return true;
    return Boolean(more) && items.length < total();
  }

  async #step(delta) {
    const lb = this.#lb;
    if (lb.busy || !this.#dialog?.open) return;
    const next = lb.index + delta;
    if (next < 0) return;
    if (next >= lb.items.length) {
      if (!this.#canNext() || !lb.more) return;
      lb.busy = true;
      await lb.more();
      lb.busy = false;
      if (next >= lb.items.length) return;
    }
    lb.index = next;
    this.#renderLightbox();
  }

  #renderLightbox() {
    const dialog = this.#dialog;
    const { items, index, total } = this.#lb;
    const item = items[index];
    if (!dialog || !item) return;
    const count = Math.max(total(), items.length);

    const img = dialog.querySelector('[data-rv-lb-img]');
    if (img instanceof HTMLImageElement) {
      img.src = item.url;
      img.alt = altFor(item.review);
    }
    const pos = dialog.querySelector('[data-rv-lb-pos]');
    if (pos) pos.textContent = count > 1 ? `${fmt(index + 1)} / ${fmt(count)}` : '';
    dialog.setAttribute('aria-label', count > 1 ? `Customer photo ${index + 1} of ${count}` : 'Customer photo');

    const prev = dialog.querySelector('[data-rv-lb-prev]');
    const next = dialog.querySelector('[data-rv-lb-next]');
    const active = document.activeElement;
    if (prev instanceof HTMLButtonElement) {
      prev.disabled = index === 0;
      prev.hidden = count < 2;
    }
    if (next instanceof HTMLButtonElement) {
      next.disabled = !this.#canNext();
      next.hidden = count < 2;
    }
    // A nav button that just became disabled would drop focus out of the dialog.
    if (active instanceof HTMLButtonElement && active.disabled && dialog.contains(active)) {
      const other = active === prev ? next : prev;
      (other instanceof HTMLButtonElement && !other.disabled && !other.hidden ? other : dialog.querySelector('[data-rv-lb-close]'))?.focus();
    }

    const panel = dialog.querySelector('[data-rv-lb-review]');
    if (panel) {
      panel.replaceChildren(this.#card(item.review, { full: true }));
      panel.scrollTop = 0;
    }

    // Warm the neighbours so stepping feels instant.
    for (const n of [items[index + 1], items[index - 1]]) {
      if (n) new Image().src = n.url;
    }
  }

  /**
   * The review panel is a focusable region (Tab reaches it and the browser scrolls it). While
   * focus is on a control instead, ↑ / ↓ / Page Up / Page Down still scroll the review text,
   * since those keys do nothing on a button.
   */
  #scrollReview(event) {
    const panel = this.#dialog?.querySelector('[data-rv-lb-review]');
    const active = document.activeElement;
    if (!(panel instanceof HTMLElement) || (active instanceof Node && panel.contains(active))) return;
    if (panel.scrollHeight <= panel.clientHeight + 1) return;
    event.preventDefault();
    const pageStep = Math.max(40, panel.clientHeight - 40);
    const step = { ArrowUp: -40, ArrowDown: 40, PageUp: -pageStep, PageDown: pageStep }[event.key] || 0;
    panel.scrollBy({ top: step });
  }

  #trapFocus(event) {
    const dialog = this.#dialog;
    if (!dialog) return;
    const focusable = Array.from(
      dialog.querySelectorAll('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((node) => node instanceof HTMLElement && !node.hidden && node.getClientRects().length > 0);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('dm-reviews')) {
  customElements.define('dm-reviews', DmReviews);
}
